import React from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { adminQueries, patchSociety } from '../../../../../../api/admin'
import {
  Button,
  ButtonVariant,
} from '../../../../../../components/common/Button'
import { FormControl } from '../../../../../../components/common/FormControl'
import {
  SelectDropdown,
  SelectOption,
} from '../../../../../../components/common/SelectDropdown'
import { ToastType } from '../../../../../../components/common/Toast/toast-type'
import { useToastNotifications } from '../../../../../../hooks/useToastNotification'
import { useAppTranslation } from '../../../../../../hooks/useAppTranslation'
import { useAdminSociety } from '../../useAdminSociety'

interface SetDiscountCodeModalContentProps {
  close: () => void
  societyId: string | undefined
}

export const SetDiscountCodeModalContent = ({
  close,
  societyId,
}: SetDiscountCodeModalContentProps): React.ReactNode => {
  const { adminSociety } = useAdminSociety(societyId)
  const { data: discountCodes } = useQuery(adminQueries.getDiscountCodes())
  const { setToastNotification } = useToastNotifications()
  const { translate } = useAppTranslation()
  const [selectedCode, setSelectedCode] = React.useState<string>('')

  const currentDiscountCode = adminSociety?.discount?.code || ''

  const options = React.useMemo<SelectOption[]>(() => {
    return (
      discountCodes?.map((discountCode) => ({
        value: discountCode.code,
        label: `${discountCode.code} - ${discountCode.discount}% - ${discountCode.partnerName}`,
      })) || []
    )
  }, [discountCodes])

  React.useEffect(() => {
    setSelectedCode(currentDiscountCode)
  }, [currentDiscountCode])

  const { mutate: mutatePatchSociety, isPending } = useMutation({
    mutationFn: patchSociety,
    onSuccess: () => {
      setToastNotification(ToastType.SUCCESS, 'Rabattkod uppdaterad')
      close()
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('flashMessage.somethingWentWrongError')
      )
    },
  })

  const onSave = (): void => {
    if (!societyId || selectedCode === '') {
      return
    }
    const discountCode = discountCodes?.find(
      (discount) => discount.code === selectedCode
    )
    if (!discountCode) {
      return
    }
    const activatedPartnerData = {
      ...(adminSociety?.activatedPartnerData?.[0] || {}),
      partnerId: discountCode.partnerId,
    }

    mutatePatchSociety({
      societyId,
      data: {
        discount: {
          code: selectedCode,
          createdAt: new Date(),
        },
        activatedPartnerData: [{ ...activatedPartnerData }],
      },
    })
  }

  const onClear = (): void => {
    if (!societyId) {
      return
    }

    mutatePatchSociety({
      societyId,
      data: {
        discount: null,
        activatedPartnerData: [],
      },
    })
  }

  if (!adminSociety) {
    return <p>Föreningen kunde inte hittas</p>
  }

  return (
    <div className="flex h-full w-full flex-col gap-6">
      <p>
        Välj rabattkod för <b>{adminSociety.name}</b>
      </p>

      <FormControl label="Rabattkod" name="discountCode">
        <SelectDropdown
          options={options}
          value={
            options.find((option) => option.value === selectedCode) || null
          }
          onChange={(option) => setSelectedCode(`${option?.value || ''}`)}
        />
      </FormControl>

      <div className="flex flex-wrap justify-end gap-4">
        <Button
          label="Ta bort rabattkod"
          variant={ButtonVariant.DANGER}
          onClick={onClear}
          disabled={isPending || currentDiscountCode === ''}
          loading={isPending}
        />
        <Button label="Avbryt" onClick={close} disabled={isPending} />
        <Button
          variant={ButtonVariant.PRIMARY}
          label="Spara"
          onClick={onSave}
          loading={isPending}
          disabled={isPending || selectedCode === ''}
        />
      </div>
    </div>
  )
}
